import type { ServicePlanInterval } from '../service-plan/service-plan.interface';
import type { DailyRateBasis, DailyRateSource } from './daily-rate';
import type { ListedReminderStatus } from './service-reminder-queue';
import { SERVICE_REMINDER_URGENCY } from './service-reminder.interface';
import type { ServicePlanReference, ServiceReminderUrgency } from './service-reminder.interface';

/**
 * The request and response shapes of the Service reminders screen (SWOOP-281): the fleet-wide queue, its
 * counts, and the snooze and dismiss actions. Types only: a response projection, never stored. The
 * frontoffice imports them from here (constitution I). Dates cross the wire as ISO strings.
 */

/**
 * The tabs of the queue (decision #22, SWOOP-291 WOW 4):
 * - `ALL` — the default tab, every live row, Overdue then Due now then Upcoming;
 * - `OVERDUE` / `DUE` / `UPCOMING` — one urgency of the live rows;
 * - `SNOOZED` — the live rows with status `SNOOZED` (they ALSO appear in their urgency tab, by snooze date);
 * - `COMPLETED` — the `COMPLETED` rows (the service history), newest completion first.
 *
 * `ALL`, `SNOOZED` and `COMPLETED` are request values only: a row's own urgency is never one of them.
 */
export const SERVICE_REMINDER_QUEUE_TAB = {
  ALL: 'ALL',
  COMPLETED: 'COMPLETED',
  SNOOZED: 'SNOOZED',
  ...SERVICE_REMINDER_URGENCY,
} as const;

export type ServiceReminderQueueTab = (typeof SERVICE_REMINDER_QUEUE_TAB)[keyof typeof SERVICE_REMINDER_QUEUE_TAB];

/** The filters of the queue; the body of `POST /service-reminders/stats` and, with `tab`, of `/search`. */
export interface ServiceReminderQueueFilters {
  /** Trimmed, at most 100 characters: vehicle name, plate, business id, plan, interval or operation. */
  search?: string;
  /** Matches the row's `planId`. */
  servicePlanIds?: string[];
  /** Matches the vehicle's `vehicleGroupId`. */
  vehicleGroupIds?: string[];
  /** ObjectId hex; the master selection when a site is selected (decision #27). */
  locationIds?: string[];
  /** ObjectId hex; the master selection when only a territory is selected, resolved to its locations server-side. */
  territoryIds?: string[];
}

/** The body of `POST /service-reminders/search`. `DUE` is the "Due now" tab; `ALL` lists every live row. */
export interface SearchServiceRemindersCriteria extends ServiceReminderQueueFilters {
  tab: ServiceReminderQueueTab;
}

/**
 * The tab counts under the same filters; each equals the `total` of that tab's search, except `completed`,
 * which is the KPI count of rows completed in the last 30 days, not the Completed tab's full total.
 */
export interface ServiceReminderStats {
  due: number;
  overdue: number;
  upcoming: number;
  /** Live rows with status `SNOOZED` (SWOOP-291); the Snoozed tab's total and KPI card. */
  snoozed: number;
  /** `COMPLETED` rows whose `completedAt` is within the last 30 days (SWOOP-291); the Completed KPI card. */
  completed: number;
}

/**
 * One row of the queue (SWOOP-281). Nothing here is copied onto the stored reminder: the vehicle, the plan
 * and the last reading are read at request time, and `urgency` is the row's own: the requested tab in a
 * single-tab search, the row's rank on `ALL`, `classifyQueueUrgency` in a snooze or dismiss response.
 */
export interface ServiceReminderListItem {
  /** `E`: `nextDueDate`, or `snoozedUntil` for a snoozed row. `null` only for a row with no due point. */
  compareDate: string | null;
  dueDate: string | null;
  /** The next km mark. */
  dueOdometer: number | null;
  /**
   * The daily rate behind the km grace and the projection: the vehicle's resolved rate (`resolveDailyRate`,
   * SWOOP-246); `null` when the rule has no distance part (`time_only`).
   */
  dailyRateKm: number | null;
  /** Where `dailyRateKm` comes from (SWOOP-246). `null` exactly when `dailyRateKm` is `null`. */
  dailyRateSource: DailyRateSource | null;
  /**
   * The evidence behind the rate (SWOOP-330): the two readings of an own rate, or the size and scope of a
   * cohort. `null` for the contract and default sources, and when `dailyRateKm` is `null`.
   */
  dailyRateBasis: DailyRateBasis | null;
  /**
   * Whole days from `dueFrom` to `nextDueDate`, the row's grace window ("Due 15 d before"); `null` when there
   * is no due point. Never recomputed by the client.
   */
  graceWindowDays: number | null;
  id: string;
  intervalCode: string;
  intervalName: string;
  lastReading: { date: string; odometer: number } | null;
  plan: ServicePlanReference;
  projectedDate: string | null;
  /** Whole days from today to `compareDate`, negative when late; `null` when `compareDate` is. */
  remainingDays: number | null;
  rule: Pick<ServicePlanInterval, 'mileageKm' | 'months' | 'trigger'>;
  /** Set when `status` is `SNOOZED`. */
  snooze: {
    count: number;
    max: number;
    originalDueDate: string | null;
    reason: string;
    until: string;
  } | null;
  /** Why Snooze is disabled; `null` = available. Every interval kind is snoozable (decision #15). */
  snoozeUnavailable: 'LIMIT_REACHED' | null;
  status: ListedReminderStatus;
  /** When a `COMPLETED` row was completed (ISO, SWOOP-291); `null` on every live row. The Completed tab sorts by it. */
  completedAt: string | null;
  /** The odometer read when the completing work order closed (SWOOP-291); `null` on every live row. */
  completedOdometer: number | null;
  /** The row's own urgency: the requested tab for `OVERDUE`, `DUE` and `UPCOMING`, per row for `ALL`. */
  urgency: ServiceReminderUrgency;
  vehicle: {
    id: string;
    licensePlate: string;
    makeId: string;
    modelCode: string;
    modelId: string;
    vehicleGroupId: string;
    /** The business fleet id. */
    vehicleId: string;
    /** The vehicle's model year. */
    year: number;
  };
  /**
   * The vehicle is Under maintenance, i.e. it already has an open work order (SWOOP-304): "Open work order"
   * is then shown disabled with the reason (product decisions Q1 and Q3, 14 Oct: one open work order per vehicle).
   */
  vehicleUnderMaintenance: boolean;
  /** The row's `updatedAt`, ISO; sent back by snooze and dismiss as their compare-and-set precondition. */
  version: string;
  /**
   * The work order on this reminder: the OPEN one handling a live row (SWOOP-304), or the CLOSING one on a
   * `COMPLETED` row (SWOOP-291, the Completed tab shows its number). `null` when none.
   */
  workOrder: { id: string; number: string } | null;
}

/** The body of `POST /service-reminders/:id/snooze`. */
export interface SnoozeServiceReminderBody {
  /** Trimmed server-side, 1 to `SERVICE_REMINDER_REASON_MAX_LENGTH` characters. */
  reason: string;
  /** `YYYY-MM-DD`, after today and at most `SERVICE_REMINDER_MAX_SNOOZE_DAYS` from today. */
  until: string;
  version: string;
}

/** The body of `POST /service-reminders/:id/dismiss`. */
export interface DismissServiceReminderBody {
  /** Trimmed server-side, 1 to `SERVICE_REMINDER_REASON_MAX_LENGTH` characters. */
  reason: string;
  version: string;
}
